import { useLayoutEffect, useRef } from 'react';
import { NavLink, useLocation } from 'react-router-dom';

// Tab nav shared by the admin pages (Users / Grant History / Feedback / Featured /
// Service Health) —
export function AdminTabs() {
  const navRef = useRef<HTMLElement>(null);
  const { pathname } = useLocation();

  /*
   * Bring the tab you are on into view.
   *
   * Below 640px the strip is a horizontal scroller with its scrollbar hidden
   * (see admin.css): five tabs are 426px against a 356px content box, and the
   * alternative was `.shell__content`'s `overflow: clip` cutting the last two
   * off with nothing that could scroll them back. A scroller starts at
   * scrollLeft 0 though, so /admin/featured and /admin/sli would load showing
   * "Users … Feedback" and no indication that the highlighted tab exists —
   * which is a worse lie than the clipping, because it looks fine.
   *
   * `aria-current="page"` rather than the `.active` class: NavLink sets both,
   * and only one of them is an API. `block: 'nearest'` keeps this horizontal in
   * practice — the tabs are at the top of the page, already vertically visible,
   * so the vertical half is a no-op and the page does not jump. On a desktop,
   * where the strip does not overflow, the whole call is a no-op.
   *
   * jsdom implements no `scrollIntoView` at all (the property is simply
   * absent), so the guard is what keeps every admin page test from throwing
   * here rather than defensive coding for a hypothetical.
   *
   * `useLayoutEffect`, not `useEffect`: a passive effect runs after the browser
   * has painted, so a route change would show the strip at its old scroll
   * position for a frame before it snapped to the new tab. That is a smaller
   * version of the bug this whole component exists to fix — the tab looking
   * wrong before it looks right — and it is the same shape as the flash
   * `useMediaQuery` was rewritten onto `useSyncExternalStore` to avoid. The
   * scroll is a visual correction to the commit that just happened, so it
   * belongs before paint. Cheap enough to be safe there: one `querySelector`
   * over five links and a call the browser no-ops when nothing overflows.
   */
  useLayoutEffect(() => {
    const active = navRef.current?.querySelector<HTMLElement>('a[aria-current="page"]');
    if (!active || typeof active.scrollIntoView !== 'function') return;
    active.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [pathname]);

  return (
    <nav className="admin-tabs" aria-label="Admin sections" ref={navRef}>
      <NavLink to="/admin" end>
        Users
      </NavLink>
      <NavLink to="/admin/grants">Grant History</NavLink>
      <NavLink to="/admin/feedback">Feedback</NavLink>
      <NavLink to="/admin/featured">Featured</NavLink>
      <NavLink to="/admin/sli">Service Health</NavLink>
    </nav>
  );
}

export default AdminTabs;
